/**
 * defa.views.CustomerCreateForm
 * A card for creating a new Customer.
 * @version 1.2.0
 */
import { defa } from 'defa-object';
import Form     from 'scripts/poppet/form';
import View     from 'scripts/poppet/view';
import template from 'templates/customerCreateForm.jst.html';

const channelUser = Bb.Radio.channel( 'user' );
const channel     = Bb.Radio.channel( 'objects' );

const selectors = {
	inputType: '[data-input=customer-type]',
	
	inputsPrivate: '[data-input=customer-first-name], [data-input=customer-last-name]',
	inputsCompany: '[data-input=customer-company-name], [data-input=customer-company-number]',
	
	inputFirstName    : '[data-input=customer-first-name]',
	inputLastName     : '[data-input=customer-last-name]',
	inputCompanyName  : '[data-input=customer-company-name]',
	inputCompanyNumber: '[data-input=customer-company-number]',
	
	inputDay  : '[data-input=customer-birth-date-day]',
	inputMonth: '[data-input=customer-birth-date-month]',
	inputYear : '[data-input=customer-birth-date-year]',
	
	inputContactEmail: '[data-input=customer-contact-mail]',
	
	inputAddress1: '[data-input=customer-address-1]',
	inputAddress2: '[data-input=customer-address-2]',
	inputPostCode: '[data-input=customer-postcode]',
	inputCity    : '[data-input=customer-city]',
	inputCountry : '[data-input=customer-country]',
	inputPhone   : '[data-input=customer-phone]',
	
	inputInvoice     : '[data-input=customer-invoice]',
	inputInvoiceEmail: '[data-input=customer-invoice-email]',
};

const CustomerCreateForm = Form.extend( {
	
	// ------------------------------------------------
	// Setup
	// ------------------------------------------------
	
	template : template,
	className: 'card has-invoice-email has-type-private',
	
	ui: {
		type: selectors.inputType,
		
		privates : selectors.inputsPrivate,
		companies: selectors.inputsCompany,
		
		firstName: selectors.inputFirstName,
		lastName : selectors.inputLastName,
		
		day  : selectors.inputDay,
		month: selectors.inputMonth,
		year : selectors.inputYear,
		
		companyName  : selectors.inputCompanyName,
		companyNumber: selectors.inputCompanyNumber,
		
		contactEmail: selectors.inputContactEmail,
		
		address1    : selectors.inputAddress1,
		address2    : selectors.inputAddress2,
		postcode    : selectors.inputPostCode,
		city        : selectors.inputCity,
		country     : selectors.inputCountry,
		invoice     : selectors.inputInvoice,
		invoiceEmail: selectors.inputInvoiceEmail,
	},
	
	events: { // we double-event for now. (IE11 supports only change events)
		'input  @ui.type'   : 'onInputType',
		'change @ui.type'   : 'onInputType',
		'input  @ui.invoice': 'onInputInvoice',
		'change @ui.invoice': 'onInputInvoice',
	},
	
	templateContext: function () {
		return {
			contactEmail: this.selectedUser.get( 'email' ) || '',
			invoiceEmail: this.selectedUser.get( 'email' ) || '',
			migrate     : this.options.migrate,
		};
	},
	
	// ------------------------------------------------
	// Event Handlers
	// ------------------------------------------------
	
	/**
	 * @constructor
	 */
	initialize: function ( options ) {
		View.prototype.initialize.call( this, options );
		
		this.selectedUser = ( this.options.userId )
			? channel.request( 'users' ).get( this.options.userId )
			: channelUser.request( 'model' );
		
		// setup debounced versions of the onInput-functions, both with 16 ms leading delay.
		this.setCompanyTypeRequires  = _.debounce( this.setCompanyTypeRequires, 16, true );
		this.onInputInvoiceDebounced = _.debounce( this.setCompanyInvoiceRequires, 16, true );
	},
	
	/**
	 * Triggers when the view renders.
	 * @void
	 */
	onRender: function () {
		this.createCountryHTML();
		this.createBirthHTML();
		
		
		if ( this.model.get( 'type' ) ) {
			this.setCompanyTypeRequires();
		}
	},
	
	/**
	 * Triggers when the type-field triggers an "input" and/or a "change" event.
	 * @void
	 */
	onInputType: function () {
		this.setCompanyTypeRequires();
	},
	
	/**
	 * Triggers when the invoice-field triggers a an "input" and/or a "change" event.
	 * @void
	 */
	onInputInvoice: function () {
		this.onInputInvoiceDebounced();
	},
	
	// ------------------------------------------------
	// Other functions
	// ------------------------------------------------
	
	/**
	 * Adjusts the required options based on the company type ( debounced by 16ms leading in initialize! ).
	 * @void
	 */
	setCompanyTypeRequires: function () {
		let input = this.ui.type.val().trim();
		
		if ( 'PRIVATE' === input ) {
			this.$el
				.toggleClass( 'has-type-private', true )
				.toggleClass( 'has-type-company', false );
			
			this.ui.privates.prop( 'required', true );
			this.ui.companies.removeAttr( 'required' );
		}
		
		if ( 'COMPANY' === input ) {
			this.$el
				.toggleClass( 'has-type-private', false )
				.toggleClass( 'has-type-company', true );
			
			this.ui.privates.removeAttr( 'required' );
			this.ui.companies.prop( 'required', true );
		}
		
	},
	
	/**
	 * Adjusts the required options based on the company invoice ( debounced by 16ms leading in initialize! ).
	 * @void
	 */
	setCompanyInvoiceRequires: function () {
		
		let input = this.ui.invoice.val().trim();
		
		if ( 'PAPER' === input ) {
			this.$el
				.toggleClass( 'has-invoice-paper', true )
				.toggleClass( 'has-invoice-email', false );
			
			this.ui.invoiceEmail.removeAttr( 'required' );
		}
		
		if ( 'EMAIL' === input ) {
			this.$el
				.toggleClass( 'has-invoice-paper', false )
				.toggleClass( 'has-invoice-email', true );
			
			this.ui.invoiceEmail.prop( 'required', true );
		}
	},
	
	/**
	 * Creates a list of html option elements to fill the country select with.
	 * @void
	 */
	createCountryHTML: function () {
		
		let self      = this,
			html5     = '',
			select    = self.ui.country,
			countries = defa.vars.countries;
		
		_.each( countries, function ( country ) {
			let selected = ( self.model.get( 'countryCode' ) === country.alpha2 )
						   ? ' selected'
						   : '';
			
			let str = '<option value="%s1"%s2>%s3</option>'
				.replace( '%s1', country.alpha2 )
				.replace( '%s2', selected )
				.replace( '%s3', country.country ); // name
			
			html5 += str;
		} );
		
		select.append( html5 );
	},
	
	/**
	 * Fills the three birth date selects.
	 * @void
	 */
	createBirthHTML: function () {
		
		let self  = this,
			date  = this.model.get( 'birthDate' ),
			day   = this.ui.day,
			month = this.ui.month,
			year  = this.ui.year,
			arr   = date.split( '-' );
		
		// first create the days:
		
		let firstDay  = 1,
			lastDay   = 31,
			daysHtml5 = '';
		
		for ( firstDay; firstDay < ( lastDay + 1 ); firstDay++ ) {
			// current day is arr[2]
			let value    = ( '0' + firstDay ).slice( -2 ),
				selected = ( arr[ 2 ] === value ) ? ' selected' : '';
			
			daysHtml5 += '<option value="%s1"%s2>%s3</option>'
				.replace( '%s1', value )
				.replace( '%s2', selected )
				.replace( '%s3', firstDay );
		}
		
		day.append( daysHtml5 );
		
		
		// then create months
		let firstMonth  = 1,
			lastMonth   = 12,
			monthsHtml5 = '';
		
		for ( firstMonth; firstMonth < ( lastMonth + 1 ); firstMonth++ ) {
			// current month is arr[1]
			let value    = ( '0' + firstMonth ).slice( -2 ),
				selected = ( arr[ 1 ] === value ) ? ' selected' : '';
			
			monthsHtml5 += '<option value="%s1"%s2>%s3</option>'
				.replace( '%s1', value )
				.replace( '%s2', selected )
				.replace( '%s3', firstMonth );
		}
		
		month.append( monthsHtml5 );
		
		
		// then create year
		let firstYear  = new Date().getFullYear(),
			lastYear   = 1900,
			yearsHtml5 = '';
		
		for ( firstYear; ( firstYear + 1 ) > lastYear; firstYear-- ) {
			// current year is arr[0]
			let value    = String( firstYear ),
				selected = ( arr[ 0 ] === value ) ? ' selected' : '';
			
			yearsHtml5 += '<option value="%s1"%s2>%s3</option>'
				.replace( '%s1', value )
				.replace( '%s2', selected )
				.replace( '%s3', firstYear );
		}
		
		year.append( yearsHtml5 );
		
	},
	
	/**
	 * Returns the customer birth date as ISO 8601 if it exists.
	 * @return {string}  An ISO 8601 date string, or an empty string.
	 */
	getBirthDate: function () {
		// check date
		let year  = this.ui.year.val().trim(),
			month = this.ui.month.val().trim(),
			day   = this.ui.day.val().trim();
		
		return ( year && month && day )
			   ? '%s1-%s2-%s3'
				   .replace( '%s1', year )
				   .replace( '%s2', month )
				   .replace( '%s3', day )
			   : '';
	},
	
	/**
	 * Submits the form
	 * @void
	 */
	submit: function () {
		
		let form = {
			type: this.ui.type.val().trim(),
			
			firstName: this.ui.firstName.val().trim(),
			lastName : this.ui.lastName.val().trim(),
			
			birthDate: this.getBirthDate(),
			
			companyName  : this.ui.companyName.val().trim(),
			companyNumber: this.ui.companyNumber.val().trim(),
			
			contactEmail: this.ui.contactEmail.val().trim(),
			
			address1   : this.ui.address1.val().trim(),
			address2   : this.ui.address2.val().trim(),
			postcode   : this.ui.postcode.val().trim(),
			city       : this.ui.city.val().trim(),
			countryCode: this.ui.country.val().trim(),
			
			invoiceTypes: [ this.ui.invoice.val().trim(), ],
			invoiceEmail: this.ui.invoiceEmail.val().trim(),
			
			// add the user
			transientUser: this.options.userId,
		};
		
		if ( 'PRIVATE' === form.type ) {
			delete form.companyName;
			delete form.companyNumber;
		}
		
		if ( 'COMPANY' === form.type ) {
			delete form.firstName;
			delete form.lastName;
			delete form.birthDate;
		}
		
		this.model.save( form );
	},
	
} );

export default CustomerCreateForm;
